import {
    NewTabScoringFormKind,
    ReadingFormScoring,
    ReadingFormScoringComponent,
} from '../reading-form-scoring.component';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {Observable, of, Subject} from 'rxjs';
import { distinctUntilChanged, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { BasicResponse } from '../../../core/interfaces/basic-response';
import { SetPageHeaderTitle } from '../../../core/data-management/actions/projects.action';
import { Store } from '@ngxs/store';
import {AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators} from '@angular/forms';

import { MatDialog } from '@angular/material/dialog';
import { ToastOptions, ToastyService } from 'ng2-toasty';
import { ReadingLevel } from '../../../core/constants/reading-level';
import { VisitStatus } from '../../../_models/ImagingProject/BRAIN-SAFETY/brain-safety-config-model';
import { ReadingBrainSafetyService } from '../../../_services/reading-brain-safety.service';
import { ConfirmSigningDialogComponent } from '../../ReportSign/confirm-signing-dialog/confirm-signing-dialog.component';
import {
    BRAIN_SAFETY_LOCATION_OPTIONS,
    normalizeBrainSafetySavedScoring,
} from './brain-safety-scoring-form.utils';

@Component({
    selector: 'app-brain-safety-scoring-form',
    templateUrl: './brain-safety-scoring-form.component.html',
    styleUrls: ['./brain-safety-scoring-form.component.css'],
})
export class BrainSafetyFormComponent
    extends ReadingFormScoringComponent
    implements OnInit, OnDestroy, ReadingFormScoring {
    // ✅ single destroy subject (prevents leaks + duplicate subscriptions)
    private destroy$ = new Subject<void>();

    constructor(
        private store: Store,
        private fb: FormBuilder,
        private readingService: ReadingBrainSafetyService,
        private dialog: MatDialog,
        private toastService: ToastyService
    ) {
        super();
    }

    currentReading: any = {
        studyId: 'NA',
        patientId: 'NA',
        visit: { id: 'NA', name: 'NA' },
        aquisitionDate: 'NA',
        visits: [],
    };

    toastOptions: ToastOptions = {
        title: '',
        showClose: true,
        timeout: 10000,
        theme: 'material',
    };

    hiddenForm = false;

    // Keep this if you need it elsewhere; template binds to scoringForm
    readingForm: FormGroup = this.fb.group({});
    scoringForm!: FormGroup;

    previousImageQuality: string;
    formChanged = false;
    isSaving = false;
    brainSafetyLocationOptions = BRAIN_SAFETY_LOCATION_OPTIONS;

    private activeVisit: any; // latest active visit snapshot

    ngOnInit(): void {
        this.store.dispatch(new SetPageHeaderTitle('Brain Structural Findings'));
        this.newTabScoringFormEnabledSubject.next(
            NewTabScoringFormKind.ATTACHED_DETACHED
        );

        // Active visit snapshot
        this.activedVisitSubject
            .pipe(takeUntil(this.destroy$))
            .subscribe((v) => (this.activeVisit = v));

        // Hide/show scoring form
        this.hideScoringFormSubject
            .pipe(takeUntil(this.destroy$))
            .subscribe((hidden) => (this.hiddenForm = hidden));

        // Once reading series is initiated, auto-select IN_PROGRESS visit
        this.readingSeriesInitiated
            .pipe(takeUntil(this.destroy$))
            .subscribe((resp) => {
                if (resp) {
                    this.chooseActiveVisitByStatus();
                }
            });

        // When current reading changes -> rebuild form + subscribe to value changes
        this.initOnCurrentReading();
    }

    private initOnCurrentReading(): void {
        this.currentReadingSubject
            .pipe(takeUntil(this.destroy$))
            .subscribe((currentReading) => {
                this.currentReading = currentReading;
                if (currentReading?.readingLevel === ReadingLevel.VISIT) {
                    this.switchSubmitBtnDisabledSubject.next(false);
                }

                // reset & rebuild
                this.readingForm = new FormGroup({});
                this.initReadingForm();
            });
    }

    private initReadingForm(): void {
        this.scoringForm = this.initScoringForm();

        // Patch any already-saved scoring from backend (keeps answers if user revisits)
        const saved = this.currentReading?.SCORING || this.currentReading?.scoring || null;
        if (saved) {
            const normalizedSavedScoring = normalizeBrainSafetySavedScoring(saved);
            this.scoringForm.patchValue({ SCORING: normalizedSavedScoring }, { emitEvent: false });
        }

        this.scoringForm.statusChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(status => {
                this.switchSubmitBtnDisabledSubject.next(status === 'INVALID');
            });

        this.setupImageQualityControls();
        this.setupSeverityDisables();
        this.setupLocationOtherControls();
        // Track changed flag
        this.formChanged = false;
        this.scoringForm.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => (this.formChanged = true));
        this.checkValidity(this.scoringForm);
    }

    private chooseActiveVisitByStatus(): void {
        const visits = this.currentReading?.visits || [];
        const activeVisitIndex = visits.findIndex(
            (v: any) => v.status === VisitStatus.IN_PROGRESS
        );

        if (activeVisitIndex !== -1) {
            this.onChangeActivatedVisit(activeVisitIndex);
        } else if (visits.length > 0) {
            // Optional fallback: pick first visit if none is IN_PROGRESS
            this.onChangeActivatedVisit(0);
        }
    }

    onChangeActivatedVisit(index: number): void {
        const visits = this.currentReading?.visits || [];
        if (!visits[index]) { return; }
        this.activedVisitSubject.next(visits[index]);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // -------------------------------------------------------------------------
    // ReadingFormScoring implementations
    // -------------------------------------------------------------------------
    getEndpointName(): string {
        return 'BRAINSAFETY';
    }

    loadReadings(
        studyId: number,
        readerId: number
    ): Observable<BasicResponse<any>> {
        return this.readingService.getAvailableReadings(studyId, readerId);
    }

    startReading(
        studyId: number,
        readingId: number
    ): Observable<BasicResponse<any>> {
        return this.readingService.startReading(readingId);
    }

    updateReading(
        studyId: number,
        readingId: number,
        data: { spentSeconds: number },
        justSaveTime?: boolean
    ): Observable<BasicResponse<any>> {
        this.currentReading.timeSpent = data.spentSeconds;
        this.updateReadingNavigator(this.currentReading);

        const readingData = { spentSeconds: data.spentSeconds };
        return this.readingService.updateReading(readingId, readingData);
    }

    submitReading(studyId: number, data: { spentSeconds: number; }): Observable<BasicResponse<any>> {
        this.currentReading.timeSpent = data.spentSeconds;
        const scoringPayload = this.getScoringPayload();
        const scoringFormData = this.buildScoringFormData(scoringPayload);
        const dialogRef = this.dialog.open(ConfirmSigningDialogComponent, {
            width: '500px',
            data: {
                title: 'Image Analysis Group requires you to authenticate your signature on this document.',
                studyId: this.currentReading.studyId,
                patientId: this.currentReading.patientId,
            }
        });
        return dialogRef.afterClosed()
            .pipe(
                switchMap((dialogResponse) => {
                    if (!dialogResponse?.jwt) {
                        if (!dialogResponse?.canceled) {
                            this.toastOptions.title = 'ERROR: Sign Report Failed.<br/>Please enter valid credentials';
                            this.toastService.error(this.toastOptions);
                        }

                        return of(null);
                    }

                    return this.readingService.completeReading(this.currentReading.id, scoringFormData, dialogResponse.jwt);
                })
            );
    }

    private checkValidity(formControl) {
        this.switchSubmitBtnDisabledSubject.next(formControl.invalid);
    }

    updateReadingNavigator(reading: any): void {
        if (!this.readingListUpdatedSubject.closed) {
            this.readingListUpdatedSubject.next(reading);
        }
    }

    // -------------------------------------------------------------------------
    // Form builder
    // -------------------------------------------------------------------------
    private initScoringForm(): FormGroup {
        return this.fb.group({
            SCORING: this.fb.group({
                imageQuality: this.fb.group(
                    {
                        qualitySuitable: this.fb.group({
                            value: new FormControl(null, Validators.required), // Yes/No/null
                        }),
                        qualityIssues: this.fb.group({
                            motionArtefact: new FormControl(false),
                            metalArtefact: new FormControl(false),
                            missingSequences: new FormControl(false),
                            other: new FormControl(false),
                            otherText: new FormControl(''),
                        }),
                    },
                    { validators: this.imageQualityIssuesRequired.bind(this) }
                ),

                safetyAssessment: this.fb.group({
                    // Radio Yes/No + comment
                    microhemorrhages: this.fb.group({
                        value: new FormControl(null, Validators.required),
                        severity: new FormControl(null),
                    }),
                    macrohemorrhages: this.fb.group({
                        value: new FormControl(null, Validators.required),
                        location: new FormControl([]),
                        otherLocation: new FormControl(''),
                    }),
                    lacunarInfarcts: this.fb.group({
                        value: new FormControl(null, Validators.required),
                        location: new FormControl([]),
                        otherLocation: new FormControl(''),
                    }),
                    largeVesselTerritorialInfarctions: this.fb.group({
                        value: new FormControl(null, Validators.required),
                        location: new FormControl([]),
                        otherLocation: new FormControl(''),
                    }),
                    microangiopathy: this.fb.group({
                        value: new FormControl(null, Validators.required),
                        severity: new FormControl(null),
                    }),
                    vasogenicEdema: this.fb.group({
                        value: new FormControl(null, Validators.required),
                        location: new FormControl([]),
                        otherLocation: new FormControl(''),
                    }),
                    acuteIschemia: this.fb.group({
                        value: new FormControl(null, Validators.required),
                        location: new FormControl([]),
                        otherLocation: new FormControl(''),
                    }),
                }),

                abnormalities: this.fb.group({
                    tumor: this.fb.group(
                        {
                            value: new FormControl(null, Validators.required),
                            location: new FormControl([]),
                            otherLocation: new FormControl(''),
                        },
                        { validators: this.locationRequiredWhenYes('value', 'location', 'otherLocation') }
                    ),
                    hydrocephalus: this.fb.group({ value: new FormControl(null, Validators.required) }),
                    vascularMalformation: this.fb.group(
                        {
                            value: new FormControl(null, Validators.required),
                            location: new FormControl([]),
                            otherLocation: new FormControl(''),
                        },
                        { validators: this.locationRequiredWhenYes('value', 'location', 'otherLocation') }
                    ),
                }),

                comment: new FormControl(''),
            }),
        });
    }

    // -------------------------------------------------------------------------
    // Save scoring
    // -------------------------------------------------------------------------
    saveScoring(): void {
        if (!this.scoringForm) { return; }

        this.markImageQualityIssuesTouched();

        // Reading + active visit context
        const readingId = this.currentReading?.id ?? this.currentReading?.readingId;
        const visitId = this.activeVisit?.id ?? this.currentReading?.visit?.id;

        if (!readingId) {
            this.toastService.error('Unable to save: readingId is missing.');
            return;
        }
        if (!visitId) {
            this.toastService.error('Unable to save: active visit is missing.');
            return;
        }

        // Payload (only SCORING root)
        const scoringPayload = this.getScoringPayload();

        // Track previous image quality if needed
        const currentQuality =
            scoringPayload?.imageQuality?.qualitySuitable?.value ?? null;
        this.previousImageQuality = currentQuality;

        this.isSaving = true;

        const model = this.buildScoringFormData(scoringPayload);

        this.readingService
            .saveReadingForm(readingId, visitId, model)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.isSaving = false;
                    this.formChanged = false;

                    // Keep local model in sync (navigator / UI)
                    this.currentReading.SCORING = scoringPayload;
                    if (this.activeVisit) { this.activeVisit.SCORING = scoringPayload; }
                    this.updateReadingNavigator(this.currentReading);

                    this.toastOptions.title = 'SUCCESS: Scoring form is saved successfully';
                    this.toastService.success(this.toastOptions);
                },
                error: (err) => {
                    this.isSaving = false;
                    // eslint-disable-next-line no-console
                    console.error('saveScoring failed', err);
                    this.toastOptions.title = 'ERROR: ' + err.message;
                    this.toastService.error(this.toastOptions);
                },
            });
    }

    private commentRequired(group: AbstractControl): ValidationErrors | null {
        const value = group.get('value')?.value;
        const comment = (group.get('comment')?.value || '').trim();

        return value === 'Yes' && !comment ? { commentRequired: true } : null;
    }

    isImageQualityNotSuitable(): boolean {
        return this.scoringForm?.get('SCORING.imageQuality.qualitySuitable.value')?.value === 'No';
    }

    isOtherImageQualityIssueSelected(): boolean {
        return this.scoringForm?.get('SCORING.imageQuality.qualityIssues.other')?.value === true;
    }

    shouldShowImageQualityIssueRequiredError(): boolean {
        const imageQuality = this.scoringForm?.get('SCORING.imageQuality');
        const qualityIssues = this.scoringForm?.get('SCORING.imageQuality.qualityIssues');

        return imageQuality?.hasError('qualityIssueRequired') === true && qualityIssues?.touched === true;
    }

    markImageQualityIssuesTouched(): void {
        this.scoringForm?.get('SCORING.imageQuality.qualityIssues')?.markAsTouched();
    }

    isSafetyAssessmentYes(findingName: string): boolean {
        return this.scoringForm?.get(`SCORING.safetyAssessment.${findingName}.value`)?.value === 'Yes';
    }

    isAbnormalityYes(findingName: string): boolean {
        return this.scoringForm?.get(`SCORING.abnormalities.${findingName}.value`)?.value === 'Yes';
    }

    isLocationOtherSelected(groupPath: string): boolean {
        const locationValue = this.scoringForm?.get(`${groupPath}.location`)?.value;

        return Array.isArray(locationValue) && locationValue.includes('Other (specify)');
    }

    private getScoringPayload() {
        return this.scoringForm.get('SCORING')?.value;
    }

    private buildScoringFormData(scoringPayload: any) {
        const model = {
            scoring: scoringPayload,
            imageQualityAssessment: null,
            imageQualityAssessmentComment: null,
            disagreement: false,
            timeSpent: 0,
            comment: ''
        };
        return model;
    }

    private setupSeverityDisables(): void {
        const pairs: Array<[string, string]> = [
            ['SCORING.safetyAssessment.microhemorrhages.value', 'SCORING.safetyAssessment.microhemorrhages.severity'],
            ['SCORING.safetyAssessment.macrohemorrhages.value', 'SCORING.safetyAssessment.macrohemorrhages.location'],
            ['SCORING.safetyAssessment.microangiopathy.value', 'SCORING.safetyAssessment.microangiopathy.severity'],
            ['SCORING.safetyAssessment.lacunarInfarcts.value', 'SCORING.safetyAssessment.lacunarInfarcts.location'],
            ['SCORING.safetyAssessment.largeVesselTerritorialInfarctions.value', 'SCORING.safetyAssessment.largeVesselTerritorialInfarctions.location'],
            ['SCORING.safetyAssessment.vasogenicEdema.value', 'SCORING.safetyAssessment.vasogenicEdema.location'],
            ['SCORING.safetyAssessment.acuteIschemia.value', 'SCORING.safetyAssessment.acuteIschemia.location'],
            ['SCORING.abnormalities.tumor.value', 'SCORING.abnormalities.tumor.location'],
            ['SCORING.abnormalities.vascularMalformation.value', 'SCORING.abnormalities.vascularMalformation.location'],
        ];

        pairs.forEach(([controller, target]) => {
            const isMultiSelectLocation = [
                'SCORING.safetyAssessment.macrohemorrhages.location',
                'SCORING.safetyAssessment.lacunarInfarcts.location',
                'SCORING.safetyAssessment.largeVesselTerritorialInfarctions.location',
                'SCORING.safetyAssessment.vasogenicEdema.location',
                'SCORING.safetyAssessment.acuteIschemia.location',
                'SCORING.abnormalities.tumor.location',
                'SCORING.abnormalities.vascularMalformation.location',
            ].includes(target);

            this.toggleControlDisabledWhen(
                controller,
                target,
                (val) => val !== 'Yes',            // disable for null + No
                {
                    resetTargetOnDisable: true,
                    resetValue: isMultiSelectLocation ? [] : null,
                    makeRequiredWhenEnabled: true,   // required only when Yes
                }
            );
        });
    }

    private setupImageQualityControls(): void {
        const qualitySuitable = this.scoringForm?.get('SCORING.imageQuality.qualitySuitable.value');
        const qualityIssues = this.scoringForm?.get('SCORING.imageQuality.qualityIssues') as FormGroup;
        const other = this.scoringForm?.get('SCORING.imageQuality.qualityIssues.other');
        const otherText = this.scoringForm?.get('SCORING.imageQuality.qualityIssues.otherText');
        const imageQuality = this.scoringForm?.get('SCORING.imageQuality');

        if (!qualitySuitable || !qualityIssues || !other || !otherText || !imageQuality) {
            return;
        }

        qualitySuitable.valueChanges
            .pipe(
                startWith(qualitySuitable.value),
                distinctUntilChanged(),
                takeUntil(this.destroy$)
            )
            .subscribe((value) => {
                if (value === 'No') {
                    qualityIssues.enable({ emitEvent: false });
                    this.toggleOtherImageQualityText(other.value === true, otherText);
                } else {
                    qualityIssues.reset(
                        {
                            motionArtefact: false,
                            metalArtefact: false,
                            missingSequences: false,
                            other: false,
                            otherText: '',
                        },
                        { emitEvent: false }
                    );
                    qualityIssues.disable({ emitEvent: false });
                }

                imageQuality.updateValueAndValidity({ emitEvent: false });
            });

        other.valueChanges
            .pipe(
                startWith(other.value),
                distinctUntilChanged(),
                takeUntil(this.destroy$)
            )
            .subscribe((checked) => {
                this.toggleOtherImageQualityText(checked === true, otherText);
                imageQuality.updateValueAndValidity({ emitEvent: false });
            });
    }

    private setupLocationOtherControls(): void {
        const locationGroupPaths = [
            'SCORING.safetyAssessment.macrohemorrhages',
            'SCORING.safetyAssessment.lacunarInfarcts',
            'SCORING.safetyAssessment.largeVesselTerritorialInfarctions',
            'SCORING.safetyAssessment.vasogenicEdema',
            'SCORING.safetyAssessment.acuteIschemia',
            'SCORING.abnormalities.tumor',
            'SCORING.abnormalities.vascularMalformation',
        ];

        locationGroupPaths.forEach((groupPath) => {
            const group = this.scoringForm?.get(groupPath);
            const location = group?.get('location');
            const otherLocation = group?.get('otherLocation');

            if (!group || !location || !otherLocation) {
                return;
            }

            location.valueChanges
                .pipe(
                    startWith(location.value),
                    distinctUntilChanged(),
                    takeUntil(this.destroy$)
                )
                .subscribe((value) => {
                    const hasOther = Array.isArray(value) && value.includes('Other (specify)');

                    this.toggleLocationOtherText(hasOther, otherLocation);
                    group.updateValueAndValidity({ emitEvent: false });
                });
        });
    }

    private toggleOtherImageQualityText(enabled: boolean, otherText: AbstractControl): void {
        if (enabled) {
            otherText.enable({ emitEvent: false });
            otherText.setValidators([Validators.required]);
        } else {
            otherText.reset('', { emitEvent: false });
            otherText.clearValidators();
            otherText.disable({ emitEvent: false });
        }

        otherText.updateValueAndValidity({ emitEvent: false });
    }

    private toggleLocationOtherText(enabled: boolean, otherText: AbstractControl): void {
        if (enabled) {
            otherText.enable({ emitEvent: false });
            otherText.setValidators([Validators.required]);
        } else {
            otherText.reset('', { emitEvent: false });
            otherText.clearValidators();
            otherText.disable({ emitEvent: false });
        }

        otherText.updateValueAndValidity({ emitEvent: false });
    }

    private imageQualityIssuesRequired(group: AbstractControl): ValidationErrors | null {
        const value = group.get('qualitySuitable.value')?.value;

        if (value !== 'No') {
            return null;
        }

        const qualityIssues = group.get('qualityIssues');
        const hasIssue = [
            'motionArtefact',
            'metalArtefact',
            'missingSequences',
            'other',
        ].some((key) => qualityIssues?.get(key)?.value === true);

        if (!hasIssue) {
            return { qualityIssueRequired: true };
        }

        const other = qualityIssues?.get('other')?.value === true;
        const otherText = (qualityIssues?.get('otherText')?.value || '').trim();

        return other && !otherText ? { otherImageQualityIssueRequired: true } : null;
    }

    private toggleControlDisabledWhen(
        controllerPath: string,
        targetPath: string,
        shouldDisable: (controllerValue: any) => boolean,
        opts?: {
            resetTargetOnDisable?: boolean;
            resetValue?: any;
            makeRequiredWhenEnabled?: boolean; // ✅ NEW
        }
    ): void {
        const controller = this.scoringForm?.get(controllerPath);
        const target = this.scoringForm?.get(targetPath);
        const otherLocation = targetPath.endsWith('.location')
            ? this.scoringForm?.get(targetPath.replace(/\.location$/, '.otherLocation'))
            : null;

        if (!controller || !target) {
            return;
        }

        const apply = (val: any) => {
            const disable = shouldDisable(val);

            if (disable) {
                if (opts?.resetTargetOnDisable) {
                    target.reset(opts?.resetValue ?? null, { emitEvent: false });
                }
                target.clearValidators();              // ✅ remove required
                target.disable({ emitEvent: false });
                if (otherLocation) {
                    this.toggleLocationOtherText(false, otherLocation);
                }
            } else {
                target.enable({ emitEvent: false });
                if (opts?.makeRequiredWhenEnabled) {
                    target.setValidators([Validators.required]); // ✅ add required
                }
            }

            if (!disable && otherLocation) {
                const targetValue = target.value;
                const hasOther = Array.isArray(targetValue) && targetValue.includes('Other (specify)');
                this.toggleLocationOtherText(hasOther, otherLocation);
            }

            target.updateValueAndValidity({ emitEvent: false });
        };

        controller.valueChanges
            .pipe(
                startWith(controller.value),   // handles initial null
                distinctUntilChanged(),
                takeUntil(this.destroy$)
            )
            .subscribe(apply);
    }

    private locationRequiredWhenYes(
        valueKey: string,
        locationKey: string,
        otherTextKey?: string
    ) {
        return (group: AbstractControl): ValidationErrors | null => {
            const val = group.get(valueKey)?.value;
            const locationValue = group.get(locationKey)?.value;
            const hasLocation = Array.isArray(locationValue)
                ? locationValue.length > 0
                : typeof locationValue === 'string' && locationValue.trim().length > 0;
            const otherText = (group.get(otherTextKey || '')?.value || '').trim();
            const requiresOtherText = Array.isArray(locationValue) && locationValue.includes('Other (specify)');

            if (val !== 'Yes') {
                return null;
            }

            if (!hasLocation) {
                return { locationRequired: true };
            }

            return requiresOtherText && !otherText ? { otherLocationRequired: true } : null;
        };
    }

    clearForm(): void {
    }
}
